import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";

import { isTransientPollError, useErrorHandler } from "../../../../../../../_metronic/helpers/ErrorHandler";
import { useViewLandContext } from "../ViewLandContext";
import { useViewImageContext } from "../Images/ViewImageContext";
import { useMapLegendContext } from "../Helpers/MapLegend";
import { useMapLayersV2 } from "../../../../Map/MapLayersV2Context";
import * as GisDataAdapter from "../../../services/GisDataAdapter";
import { openAlert } from "../../../../../../../_metronic/elements/NotificationAlert";
import { useTranslation } from "react-i18next";
import { applyAmbientToMap } from "./ambientMapUtils";
import { clearAmbientRequest, getOrCreateAmbientRequest } from "./ambientRequestId";
import { PLANET_HD_V1_ENABLED } from "../../../../../../config/featureFlags";
import { useAuth } from "../../../../../auth/core/Auth";
import { canAccessHistoricalHd as hasHistoricalHdAccess } from "../../../../../auth/core/historicalHdAccess";
import {
  isPlanetHdJobFailed,
  isPlanetHdJobPending,
  isPlanetHdJobReady,
} from "./planetHdJobModel";
import {
  PLANET_HD_AMBIENT_MODES,
  buildPlanetHdHistoricalAmbientPayload,
} from "./planetHdAmbientRequest";
import { createDocumentScopedValue, readDocumentScopedValue } from "../landViewDocumentState";

const ViewAmbientContext = createContext();

const AMBIENT_POLL_INTERVAL_MS = 30000;
const AMBIENT_POLL_TIMEOUT_ALERT_EVERY = 5;
const AMBIENT_SELECTION_BY_LAND_STORAGE_KEY = "specific:view-land-ambient-selection-by-land";
const AMBIENT_SELECTION_TYPES = ["ambient", "ambientHd", "imageAmbient", "imageAmbientHd"];

const isAmbientSelectionType = (value) => {
  return AMBIENT_SELECTION_TYPES.includes(value);
};

const getSessionStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage || null;
  } catch (error) {
    return null;
  }
};

const getStoredAmbientSelections = () => {
  const storage = getSessionStorage();

  if (!storage) {
    return {};
  }

  try {
    const rawValue = storage.getItem(AMBIENT_SELECTION_BY_LAND_STORAGE_KEY);
    const parsed = readDocumentScopedValue(rawValue ? JSON.parse(rawValue) : null) || {};

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return Object.entries(parsed).reduce((accumulator, [landId, selectedElement]) => {
      if (isAmbientSelectionType(selectedElement)) {
        accumulator[landId] = selectedElement;
      }

      return accumulator;
    }, {});
  } catch (error) {
    return {};
  }
};

const setStoredAmbientSelections = (selectionsByLand) => {
  const storage = getSessionStorage();

  if (!storage) {
    return;
  }

  try {
    storage.setItem(
      AMBIENT_SELECTION_BY_LAND_STORAGE_KEY,
      JSON.stringify(createDocumentScopedValue(selectionsByLand))
    );
  } catch (error) {
    // Session storage can be unavailable in restricted browser modes.
  }
};

export function useViewAmbientContext() {
  return useContext(ViewAmbientContext);
}

export const ViewAmbientConsumer = ViewAmbientContext.Consumer;

export function ViewAmbientProvider({ children }) {
  const { currentUser } = useAuth();
  const canAccessHistoricalHd = PLANET_HD_V1_ENABLED && hasHistoricalHdAccess(currentUser);
  const historicalHdAccessRef = useRef(canAccessHistoricalHd);
  historicalHdAccessRef.current = canAccessHistoricalHd;
  const [loading, setLoading] = useState(true);
  const [ambientLoaded, setAmbientLoaded] = useState(false);
  const [ambientHdLoaded, setAmbientHdLoaded] = useState(false);
  const [imageAmbientLoaded, setImageAmbientLoaded] = useState(false);
  const [imageAmbientHdLoaded, setImageAmbientHdLoaded] = useState(false);
  const [createTriggered, setCreateTriggered] = useState();
  const [ambientData, setAmbientData] = useState(null);
  const [storedAmbientHdData, setAmbientHdData] = useState(null);
  const ambientHdData = canAccessHistoricalHd ? storedAmbientHdData : null;
  const [imageAmbientData, setImageAmbientData] = useState(null);
  const [imageAmbientHdData, setImageAmbientHdData] = useState(null);
  const [ambientDialog, setAmbientDialog] = useState(false);
  const [ambientDialogMode, setAmbientDialogMode] = useState("standard");
  const [deleteAmbient, setDeleteAmbient] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const { errorHandler } = useErrorHandler();
  const { t } = useTranslation();
  const timeout = useRef(null);
  const timeoutHd = useRef(null);
  const timeoutImage = useRef(null);
  const timeoutImageHd = useRef(null);
  const activeRequestId = useRef(0);
  const currentLandId = useRef(null);
  const selectedElementByLand = useRef(getStoredAmbientSelections());
  const selectedElementRef = useRef(null);
  const ambientDataRef = useRef(null);
  const ambientHdDataRef = useRef(null);
  const imageAmbientHdDataRef = useRef(null);
  const imageAmbientDataRef = useRef(null);
  const ambientPollTimeoutCount = useRef(0);
  const isAmbientPollPendingRef = useRef(false);
  const isAmbientHdPollPendingRef = useRef(false);
  const isImageAmbientPollPendingRef = useRef(false);
  const isImageAmbientHdPollPendingRef = useRef(false);
  const legacyCreateInFlight = useRef(false);
  const hdCreateInFlight = useRef(false);
  const loadAmbientDataRef = useRef(null);
  const loadAmbientHdDataRef = useRef(null);
  const loadImageAmbientDataRef = useRef(null);
  const loadImageAmbientHdDataRef = useRef(null);
  const viewLandContext = useViewLandContext();
  const previousActiveSection = useRef(viewLandContext.activeSection);
  const viewImageContext = useViewImageContext();
  const mapLegendContext = useMapLegendContext();
  const mapLayers = useMapLayersV2();
  const { setActiveAmbientSelection, addOpenedAmbients } = viewImageContext;

  const persistSelectedElement = useCallback((landId, nextSelectedElement) => {
    if (!landId) {
      return;
    }

    if (isAmbientSelectionType(nextSelectedElement)) {
      selectedElementByLand.current[landId] = nextSelectedElement;
      setStoredAmbientSelections(selectedElementByLand.current);
      return;
    }

    delete selectedElementByLand.current[landId];
    setStoredAmbientSelections(selectedElementByLand.current);
  }, []);

  const setSelectedElementForCurrentLand = useCallback((nextSelectedElement) => {
    if (nextSelectedElement === "ambientHd" && !historicalHdAccessRef.current) return;
    const landId = currentLandId.current || viewLandContext.land?.land_id;

    persistSelectedElement(landId, nextSelectedElement);
    setSelectedElement(nextSelectedElement);
  }, [persistSelectedElement, viewLandContext.land?.land_id]);

  const buildQueuedAmbientData = useCallback(({ ambientId, monthRange, yearsList, countAmbients, quality = "standard" }) => {
    const defaultYears = [];
    for (let year = 2013; year <= new Date().getFullYear(); year += 1) {
      defaultYears.push(year);
    }

    return {
      ambient_id: ambientId || null,
      status: 0,
      progress: 0,
      editable: false,
      params: {
        month_range: monthRange || ["10", "05"],
        years: yearsList || defaultYears,
        breaks: countAmbients || 5,
        quality,
      },
    };
  }, []);

  const buildQueuedImageAmbientData = useCallback(({
    acquisitionId,
    ambientId,
    basis,
    layer,
    layerDate,
    numClasses,
    quality = "standard",
  }) => {
    return {
      ambient_id: ambientId || null,
      status: 0,
      progress: 0,
      editable: false,
      params: {
        layer: layer || "evi",
        layer_date: layerDate || null,
        breaks: numClasses || 2,
        quality,
        ...(basis ? { basis } : {}),
        ...(acquisitionId ? { acquisition_id: acquisitionId } : {}),
      },
    };
  }, []);

  const clearRefreshTimeout = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    if (timeoutHd.current) {
      clearTimeout(timeoutHd.current);
      timeoutHd.current = null;
    }

    if (timeoutImage.current) {
      clearTimeout(timeoutImage.current);
      timeoutImage.current = null;
    }

    if (timeoutImageHd.current) {
      clearTimeout(timeoutImageHd.current);
      timeoutImageHd.current = null;
    }
  }, []);

  const scheduleAmbientPoll = useCallback(({ landId = currentLandId.current, delay = AMBIENT_POLL_INTERVAL_MS, target = "standard" } = {}) => {
    if (!landId || (!PLANET_HD_V1_ENABLED && (target === "hd" || target === "image_hd")) || (target === "hd" && !historicalHdAccessRef.current)) {
      return;
    }

    const timeoutRef = target === "hd"
      ? timeoutHd
      : target === "image"
        ? timeoutImage
      : target === "image_hd"
        ? timeoutImageHd
        : timeout;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    timeoutRef.current = setTimeout(() => {
      const loader = target === "hd"
        ? loadAmbientHdDataRef.current
        : target === "image"
          ? loadImageAmbientDataRef.current
        : target === "image_hd"
          ? loadImageAmbientHdDataRef.current
          : loadAmbientDataRef.current;
      loader?.({
        landId,
        requestId: activeRequestId.current,
        allowPolling: true,
      })?.catch?.(() => {});
    }, delay);
  }, []);

  const requestAmbientData = useCallback(async (landId, getAmbient = GisDataAdapter.getLandAmbient) => {
    try {
      const res = await getAmbient(landId);

      if (res?.ok) {
        return res;
      }

      return { ok: false, error_type: "transient" };
    } catch (error) {
      if (isTransientPollError(error)) {
        return { ok: false, error_type: "transient" };
      }

      if (error?.response?.status === 404) {
        return { ok: false, error_type: "not_found" };
      }

      return { ok: false, error_type: "internal_error" };
    }
  }, []);

  const requestAmbientHdData = useCallback(async (landId, getAmbient = GisDataAdapter.getLandAmbientHd) => {
    try {
      const res = await getAmbient(landId);

      if (res?.ok) {
        return res;
      }

      return { ok: false, error_type: "transient" };
    } catch (error) {
      if (isTransientPollError(error)) {
        return { ok: false, error_type: "transient" };
      }

      if (error?.response?.status === 404) {
        return { ok: false, error_type: "not_found" };
      }

      return { ok: false, error_type: "internal_error" };
    }
  }, []);

  const shouldKeepAmbientPolling = useCallback((allowPolling) => {
    return allowPolling
      || ambientDataRef.current?.status === 0
      || isAmbientPollPendingRef.current;
  }, []);

  const shouldKeepAmbientHdPolling = useCallback((allowPolling) => {
    return allowPolling
      || isPlanetHdJobPending(ambientHdDataRef.current?.status)
      || isAmbientHdPollPendingRef.current;
  }, []);

  const notifyAmbientPollTimeouts = useCallback(() => {
    ambientPollTimeoutCount.current += 1;

    if (
      ambientPollTimeoutCount.current >= AMBIENT_POLL_TIMEOUT_ALERT_EVERY
      && ambientPollTimeoutCount.current % AMBIENT_POLL_TIMEOUT_ALERT_EVERY === 0
    ) {
      openAlert(
        "warning",
        t("ambient:create.messages.poll_timeout.title"),
        t("ambient:create.messages.poll_timeout.message")
      );
    }
  }, [t]);

  const handleAmbientCompleted = useCallback((ambient, landId, selection = "ambient") => {
    if (!ambient?.geojson?.features?.length || !landId) {
      return;
    }

    openAlert(
      "success",
      t("ambient:create.messages.completed.title"),
      t("ambient:create.messages.completed.message")
    );
    setSelectedElementForCurrentLand(selection);
    applyAmbientToMap({
      ambient,
      landId,
      mapLayers,
      mapLegendContext,
      addOpenedAmbients,
    });
  }, [addOpenedAmbients, mapLayers, mapLegendContext, setSelectedElementForCurrentLand, t]);

  const loadAmbientData = useCallback(async ({ landId = currentLandId.current, requestId = activeRequestId.current, allowPolling = true } = {}) => {
    if (!landId) {
      return null;
    }

    const res = await requestAmbientData(landId);

    if (requestId !== activeRequestId.current) {
      return null;
    }

    if (res?.ok) {
      ambientPollTimeoutCount.current = 0;
      const previousStatus = ambientDataRef.current?.status;
      const wasProcessing = previousStatus === 0 || isAmbientPollPendingRef.current;

      if (res.data.status === 0) {
        isAmbientPollPendingRef.current = true;

        if (allowPolling) {
          scheduleAmbientPoll({ landId });
        }
      } else {
        isAmbientPollPendingRef.current = false;
      }

      setAmbientData(res.data);
      setAmbientLoaded(true);

      if (res.data.status === 1 && wasProcessing) {
        handleAmbientCompleted(res.data, landId);
      }

      return res.data;
    }

    if (res?.error_type === "not_found") {
      if (isAmbientPollPendingRef.current || ambientDataRef.current?.status === 0) {
        scheduleAmbientPoll({landId});
        return null;
      }
      isAmbientPollPendingRef.current = false;
      setAmbientData(null);
      setAmbientLoaded(true);
      return null;
    }

    if (shouldKeepAmbientPolling(allowPolling)) {
      if (res?.error_type === "transient") {
        notifyAmbientPollTimeouts();
      }

      scheduleAmbientPoll({ landId });
      return null;
    }

    if (res?.error_type === "internal_error") {
      openAlert("danger", "Error", "Hubo un error al procesar la solicitud");
    }

    isAmbientPollPendingRef.current = false;
    setAmbientData(null);
    setAmbientLoaded(true);
    return null;
  }, [handleAmbientCompleted, notifyAmbientPollTimeouts, requestAmbientData, scheduleAmbientPoll, shouldKeepAmbientPolling]);

  const loadAmbientHdData = useCallback(async ({ landId = currentLandId.current, requestId = activeRequestId.current, allowPolling = true } = {}) => {
    if (!historicalHdAccessRef.current || !landId) {
      isAmbientHdPollPendingRef.current = false;
      setAmbientHdData(null);
      setAmbientHdLoaded(true);
      return null;
    }

    const res = await requestAmbientHdData(landId);

    if (requestId !== activeRequestId.current || !historicalHdAccessRef.current) {
      return null;
    }

    if (res?.ok) {
      const previousStatus = ambientHdDataRef.current?.status;
      const wasProcessing = isPlanetHdJobPending(previousStatus) || isAmbientHdPollPendingRef.current;

      if (isPlanetHdJobPending(res.data.status)) {
        isAmbientHdPollPendingRef.current = true;

        if (allowPolling) {
          scheduleAmbientPoll({ landId, target: "hd" });
        }
      } else {
        isAmbientHdPollPendingRef.current = false;
      }

      setAmbientHdData(res.data);
      setAmbientHdLoaded(true);

      if (isPlanetHdJobReady(res.data.status) && wasProcessing) {
        handleAmbientCompleted(res.data, landId, "ambientHd");
      }

      return res.data;
    }

    if (res?.error_type === "not_found") {
      if (isAmbientHdPollPendingRef.current || isPlanetHdJobPending(ambientHdDataRef.current?.status)) {
        scheduleAmbientPoll({landId, target: "hd"});
        return null;
      }
      isAmbientHdPollPendingRef.current = false;
      setAmbientHdData(null);
      setAmbientHdLoaded(true);
      return null;
    }

    if (shouldKeepAmbientHdPolling(allowPolling)) {
      if (res?.error_type === "transient") {
        notifyAmbientPollTimeouts();
      }

      scheduleAmbientPoll({ landId, target: "hd" });
      return null;
    }

    if (res?.error_type === "internal_error") {
      openAlert("danger", "Error", "Hubo un error al procesar la solicitud");
    }

    isAmbientHdPollPendingRef.current = false;
    setAmbientHdData(null);
    setAmbientHdLoaded(true);
    return null;
  }, [handleAmbientCompleted, notifyAmbientPollTimeouts, requestAmbientHdData, scheduleAmbientPoll, shouldKeepAmbientHdPolling]);

  const loadImageAmbientData = useCallback(async ({ landId = currentLandId.current, requestId = activeRequestId.current, allowPolling = true } = {}) => {
    if (!landId) {
      return null;
    }

    const res = await requestAmbientData(landId, GisDataAdapter.getLandImageAmbient);

    if (requestId !== activeRequestId.current) {
      return null;
    }

    if (res?.ok) {
      const wasProcessing = imageAmbientDataRef.current?.status === 0
        || isImageAmbientPollPendingRef.current;
      if (res.data.status === 0) {
        isImageAmbientPollPendingRef.current = true;
        if (allowPolling) scheduleAmbientPoll({landId, target: "image"});
      } else {
        isImageAmbientPollPendingRef.current = false;
      }
      setImageAmbientData(res.data);
      setImageAmbientLoaded(true);
      if (res.data.status === 1 && wasProcessing) {
        handleAmbientCompleted(res.data, landId, "imageAmbient");
      }
      return res.data;
    }

    if (res?.error_type === "not_found") {
      if (isImageAmbientPollPendingRef.current || imageAmbientDataRef.current?.status === 0) {
        scheduleAmbientPoll({landId, target: "image"});
        return null;
      }
      isImageAmbientPollPendingRef.current = false;
      setImageAmbientData(null);
      setImageAmbientLoaded(true);
      return null;
    }

    if (allowPolling || isImageAmbientPollPendingRef.current || imageAmbientDataRef.current?.status === 0) {
      if (res?.error_type === "transient") notifyAmbientPollTimeouts();
      scheduleAmbientPoll({landId, target: "image"});
      return null;
    }

    isImageAmbientPollPendingRef.current = false;
    setImageAmbientData(null);
    setImageAmbientLoaded(true);
    return null;
  }, [handleAmbientCompleted, notifyAmbientPollTimeouts, requestAmbientData, scheduleAmbientPoll]);

  const loadImageAmbientHdData = useCallback(async ({ landId = currentLandId.current, requestId = activeRequestId.current } = {}) => {
    if (!PLANET_HD_V1_ENABLED || !landId) {
      setImageAmbientHdData(null);
      setImageAmbientHdLoaded(true);
      return null;
    }

    const res = await requestAmbientHdData(landId, GisDataAdapter.getLandImageAmbientHd);

    if (requestId !== activeRequestId.current) {
      return null;
    }

    if (res?.ok) {
      const previousStatus = imageAmbientHdDataRef.current?.status;
      const wasProcessing = isPlanetHdJobPending(previousStatus)
        || isImageAmbientHdPollPendingRef.current;
      if (isPlanetHdJobPending(res.data.status)) {
        isImageAmbientHdPollPendingRef.current = true;
        scheduleAmbientPoll({landId, target: "image_hd"});
      } else {
        isImageAmbientHdPollPendingRef.current = false;
      }

      setImageAmbientHdData(res.data);
      setImageAmbientHdLoaded(true);

      if (isPlanetHdJobReady(res.data.status) && wasProcessing) {
        handleAmbientCompleted(res.data, landId, "imageAmbientHd");
      }

      return res.data;
    }

    if (res?.error_type === "not_found") {
      if (isImageAmbientHdPollPendingRef.current || isPlanetHdJobPending(imageAmbientHdDataRef.current?.status)) {
        scheduleAmbientPoll({landId, target: "image_hd"});
        return null;
      }
      isImageAmbientHdPollPendingRef.current = false;
      setImageAmbientHdData(null);
      setImageAmbientHdLoaded(true);
      return null;
    }

    if (isImageAmbientHdPollPendingRef.current || res?.error_type === "transient" || res?.error_type === "internal_error") {
      scheduleAmbientPoll({landId, target: "image_hd"});
      return null;
    }

    isImageAmbientHdPollPendingRef.current = false;
    setImageAmbientHdData(null);
    setImageAmbientHdLoaded(true);
    return null;
  }, [handleAmbientCompleted, requestAmbientHdData, scheduleAmbientPoll]);

  useEffect(() => {
    ambientDataRef.current = ambientData;
  }, [ambientData]);

  useEffect(() => {
    ambientHdDataRef.current = ambientHdData;
  }, [ambientHdData]);

  useEffect(() => {
    imageAmbientHdDataRef.current = imageAmbientHdData;
  }, [imageAmbientHdData]);

  useEffect(() => {
    imageAmbientDataRef.current = imageAmbientData;
  }, [imageAmbientData]);

  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  useEffect(() => {
    loadAmbientDataRef.current = loadAmbientData;
  }, [loadAmbientData]);

  useEffect(() => {
    loadAmbientHdDataRef.current = loadAmbientHdData;
  }, [loadAmbientHdData]);

  useEffect(() => {
    loadImageAmbientDataRef.current = loadImageAmbientData;
  }, [loadImageAmbientData]);

  useEffect(() => {
    loadImageAmbientHdDataRef.current = loadImageAmbientHdData;
  }, [loadImageAmbientHdData]);

  useEffect(() => {
    const landId = viewLandContext.land?.land_id;
    const previousLandId = currentLandId.current;

    if (previousLandId) {
      persistSelectedElement(previousLandId, selectedElementRef.current);
    }

    currentLandId.current = landId || null;
    const requestId = activeRequestId.current + 1;
    activeRequestId.current = requestId;
    ambientPollTimeoutCount.current = 0;
    isAmbientPollPendingRef.current = false;
    isAmbientHdPollPendingRef.current = false;
    isImageAmbientPollPendingRef.current = false;
    isImageAmbientHdPollPendingRef.current = false;
    ambientDataRef.current = null;
    ambientHdDataRef.current = null;
    imageAmbientDataRef.current = null;
    imageAmbientHdDataRef.current = null;
    clearRefreshTimeout();
    const storedSelection = landId ? selectedElementByLand.current[landId] || null : null;
    setSelectedElement(storedSelection === "ambientHd" && !canAccessHistoricalHd ? null : storedSelection);

    if (landId === null || landId === undefined) {
      setAmbientData(null);
      setAmbientHdData(null);
      setImageAmbientData(null);
      setImageAmbientHdData(null);
      setAmbientLoaded(false);
      setAmbientHdLoaded(false);
      setImageAmbientLoaded(false);
      setImageAmbientHdLoaded(false);
      setLoading(false);
      return;
    }

    setAmbientLoaded(false);
    setAmbientHdLoaded(false);
    setImageAmbientLoaded(false);
    setImageAmbientHdLoaded(false);
    setAmbientData(null);
    setAmbientHdData(null);
    setImageAmbientData(null);
    setImageAmbientHdData(null);
    setLoading(true);

    Promise.all([
      loadAmbientDataRef.current({ landId, requestId }),
      loadAmbientHdDataRef.current({ landId, requestId }),
      loadImageAmbientDataRef.current({ landId, requestId }),
      loadImageAmbientHdDataRef.current({ landId, requestId }),
    ]).finally(() => {
      if (requestId === activeRequestId.current) {
        setLoading(false);
      }
    });

    return () => {
      clearRefreshTimeout();
    };
  }, [canAccessHistoricalHd, clearRefreshTimeout, persistSelectedElement, viewLandContext.land?.land_id]);

  useEffect(() => {
    const activeSection = viewLandContext.activeSection;
    const enteredAmbients = activeSection === "ambients"
      && previousActiveSection.current !== "ambients";
    previousActiveSection.current = activeSection;

    if (!enteredAmbients || !currentLandId.current) return;

    const landId = currentLandId.current;
    const requestId = activeRequestId.current + 1;
    activeRequestId.current = requestId;
    clearRefreshTimeout();
    setAmbientLoaded(false);
    setAmbientHdLoaded(false);
    setImageAmbientLoaded(false);
    setImageAmbientHdLoaded(false);
    setLoading(true);

    Promise.all([
      loadAmbientDataRef.current({ landId, requestId }),
      loadAmbientHdDataRef.current({ landId, requestId }),
      loadImageAmbientDataRef.current({ landId, requestId }),
      loadImageAmbientHdDataRef.current({ landId, requestId }),
    ]).finally(() => {
      if (requestId === activeRequestId.current) setLoading(false);
    });
  }, [clearRefreshTimeout, viewLandContext.activeSection]);

  useEffect(() => {
    return () => {
      clearRefreshTimeout();
    };
  }, [clearRefreshTimeout]);

  useEffect(() => {
    if (viewImageContext.clearMapVersion < 1) {
      return;
    }

    selectedElementByLand.current = {};
    setStoredAmbientSelections(selectedElementByLand.current);
    setSelectedElement(null);
  }, [viewImageContext.clearMapVersion]);

  useEffect(() => {
    const currentLand = viewLandContext.land?.land_id;
    const hasHistoricalSelection = selectedElement === "ambient" && Boolean(ambientData?.geojson?.features?.length);
    const hasHistoricalHdSelection = selectedElement === "ambientHd" && Boolean(ambientHdData?.geojson?.features?.length);
    const hasImageSelection = selectedElement === "imageAmbient" && Boolean(imageAmbientData?.geojson?.features?.length);
    const hasImageHdSelection = selectedElement === "imageAmbientHd" && Boolean(imageAmbientHdData?.geojson?.features?.length);

    if (currentLand && (hasHistoricalSelection || hasHistoricalHdSelection || hasImageSelection || hasImageHdSelection)) {
      setActiveAmbientSelection({
        landId: currentLand,
        type: selectedElement,
      });
      return;
    }

    setActiveAmbientSelection(null);
  }, [
    ambientData?.geojson?.features?.length,
    ambientHdData?.geojson?.features?.length,
    imageAmbientData?.geojson?.features?.length,
    imageAmbientHdData?.geojson?.features?.length,
    selectedElement,
    setActiveAmbientSelection,
    viewLandContext.land?.land_id,
  ]);

  const submitLegacyAmbient = useCallback(async (triggered, { monthRange, yearsList, countAmbients, update }) => {
    setCreateTriggered(triggered);

    const data = {
      month_range: monthRange,
      years_list: yearsList,
      count_ambients: countAmbients
    };
    if (!monthRange) {
      data['month_range'] = ["10", "05"];
    }

    if (!yearsList) {
      const def = [];
      for (var i = 2013; i <= new Date().getFullYear(); i++) {
        def.push(i);
      }
      data['years_list'] = def;
    }

    if (!countAmbients) {
      data['count_ambients'] = 5;
    }

    const ambientRequest = getOrCreateAmbientRequest({
      landId: viewLandContext.land.land_id,
      operation: update === true ? "update" : "create",
      monthRange: data.month_range,
      yearsList: data.years_list,
      countAmbients: data.count_ambients,
    });
    data.request_id = ambientRequest.requestId;

    let res;

    if (update === true) {
      res = await errorHandler(GisDataAdapter.updateLandAmbient(
        viewLandContext.land.land_id, data
      ));
    } else {
      res = await errorHandler(GisDataAdapter.createLandAmbient(
        viewLandContext.land.land_id, data
      ));
    }

    if (res.ok) {
      clearAmbientRequest(ambientRequest.storageKey);
      openAlert("success", t("ambient:create.messages.success.title"), t("ambient:create.messages.success.message"));
      ambientPollTimeoutCount.current = 0;
      isAmbientPollPendingRef.current = true;
      const requestedAmbient = res.data || {};
      setAmbientData({
        ...buildQueuedAmbientData({ ambientId: requestedAmbient.ambient_id, monthRange, yearsList, countAmbients }),
        ...requestedAmbient,
        status: requestedAmbient.status ?? 0,
        progress: requestedAmbient.progress ?? 0,
        editable: false,
      });
      setAmbientLoaded(true);
      clearRefreshTimeout();
      scheduleAmbientPoll({
        landId: viewLandContext.land?.land_id,
        delay: 10000,
      });

      if (triggered === 'personalized') {
        setAmbientDialog(false);
      }
      setCreateTriggered(null);
    } else if (res.error_type === "need_payment") {
      setCreateTriggered(null);
      return "need_payment";
    }

    setCreateTriggered(null);
  }, [buildQueuedAmbientData, clearRefreshTimeout, errorHandler, scheduleAmbientPoll, t, viewLandContext.land]);

  const create = useCallback(async (triggered, options) => {
    if (legacyCreateInFlight.current || isAmbientPollPendingRef.current || ambientDataRef.current?.status === 0) {
      return "in_progress";
    }
    legacyCreateInFlight.current = true;
    try {
      return await submitLegacyAmbient(triggered, options);
    } finally {
      legacyCreateInFlight.current = false;
      setCreateTriggered(null);
    }
  }, [submitLegacyAmbient]);

  const submitAmbientHd = useCallback(async (triggered, {
    monthRange,
    yearsList,
    countAmbients,
    update,
  }) => {
    if (!historicalHdAccessRef.current) return false;
    setCreateTriggered(triggered);
    const isPersonalized = triggered === "personalized_hd";
    const data = buildPlanetHdHistoricalAmbientPayload({
      countAmbients: countAmbients || 5,
      mode: isPersonalized
        ? PLANET_HD_AMBIENT_MODES.CUSTOM
        : PLANET_HD_AMBIENT_MODES.AUTOMATIC,
      monthRange: monthRange || ["10", "05"],
      yearsList: Array.isArray(yearsList) ? yearsList : [],
    });
    const requestedMonthRange = isPersonalized ? data.month_range : ["10", "05"];
    const requestedYears = isPersonalized ? data.years_list : [];
    const requestedCount = isPersonalized ? data.count_ambients : 5;

    const ambientRequest = getOrCreateAmbientRequest({
      landId: viewLandContext.land.land_id,
      operation: update === true ? "update_historical_hd" : "historical_hd",
      monthRange: requestedMonthRange,
      yearsList: requestedYears,
      countAmbients: requestedCount,
    });
    data.request_id = ambientRequest.requestId;
    data.idempotency_key = ambientRequest.requestId;

    let res;
    if (update === true) {
      res = await errorHandler(GisDataAdapter.updateLandAmbientHd(
        viewLandContext.land.land_id, data
      ));
    } else {
      res = await errorHandler(GisDataAdapter.createLandAmbientHd(
        viewLandContext.land.land_id, data
      ));
    }

    if (res?.ok) {
      clearAmbientRequest(ambientRequest.storageKey);
      openAlert("success", t("ambient:create.messages.success.title"), t("ambient:create.messages.success.message"));
      isAmbientHdPollPendingRef.current = true;
      const requestedAmbient = res.data || {};
      setAmbientHdData({
        ...buildQueuedAmbientData({
          ambientId: requestedAmbient.ambient_id,
          monthRange: requestedMonthRange,
          yearsList: requestedYears,
          countAmbients: requestedCount,
          quality: "hd",
        }),
        ...requestedAmbient,
        status: requestedAmbient.status ?? 0,
        progress: requestedAmbient.progress ?? 0,
        editable: false,
      });
      setAmbientHdLoaded(true);
      scheduleAmbientPoll({
        landId: viewLandContext.land?.land_id,
        delay: 10000,
        target: "hd",
      });

      if (triggered === 'personalized_hd') {
        setAmbientDialog(false);
      }
      setCreateTriggered(null);
    } else if (res?.error_type === "need_payment") {
      setCreateTriggered(null);
      return "need_payment";
    }

    setCreateTriggered(null);
  }, [buildQueuedAmbientData, errorHandler, scheduleAmbientPoll, t, viewLandContext.land]);

  const createHd = useCallback(async (triggered, options = {}) => {
    if (!historicalHdAccessRef.current) {
      return false;
    }

    if (
      hdCreateInFlight.current ||
      isAmbientHdPollPendingRef.current ||
      isPlanetHdJobPending(ambientHdDataRef.current?.status)
    ) {
      return "in_progress";
    }

    hdCreateInFlight.current = true;
    try {
      return await viewImageContext.requestHdAccess({
        land: viewLandContext.land,
        operation: "ambient_historical_hd",
        context: {
          ambient_type: triggered === "personalized_hd" ? "personalized" : "default",
        },
        onConfirmed: () => submitAmbientHd(triggered, options),
      });
    } finally {
      hdCreateInFlight.current = false;
      setCreateTriggered(null);
    }
  }, [submitAmbientHd, viewImageContext, viewLandContext.land]);

  const [dialogInitialData, setDialogInitialData] = useState(null);
  const openAmbientDialog = (mode = "standard") => {
    if (mode === "hd" && !historicalHdAccessRef.current) return false;
    setAmbientDialogMode(mode);
    setDialogInitialData(null);
    setAmbientDialog(true);
  };
  const updateAmbientDialog = () => {
    setAmbientDialogMode("standard");
    setAmbientDialog(true);
    setDialogInitialData({
      countAmbients: ambientData.params.breaks,
      monthFrom: ambientData.params.month_range[0],
      monthTo: ambientData.params.month_range[1],
      years: ambientData.params.years
    });
  };

  const ambientSectionState = useMemo(() => {
    if (loading || !ambientLoaded || !ambientHdLoaded || !imageAmbientLoaded || !imageAmbientHdLoaded) {
      return "loading";
    }

    if (selectedElement === "imageAmbient" && imageAmbientData?.status === 0) {
      return "processing";
    }

    if (selectedElement === "imageAmbientHd" && isPlanetHdJobPending(imageAmbientHdData?.status)) {
      return "processing";
    }

    if (selectedElement === "ambientHd" && isPlanetHdJobPending(ambientHdData?.status)) {
      return "processing";
    }

    if (selectedElement === "ambient" && ambientData?.status === 0) {
      return "processing";
    }

    if (imageAmbientData?.status === 0) {
      return "processing";
    }

    if (isPlanetHdJobPending(imageAmbientHdData?.status)) {
      return "processing";
    }

    if (isPlanetHdJobPending(ambientHdData?.status)) {
      return "processing";
    }

    if (ambientData?.status === 0) {
      return "processing";
    }

    if (selectedElement === "imageAmbientHd" && isPlanetHdJobReady(imageAmbientHdData?.status)) {
      return "image_hd_ready";
    }

    if (selectedElement === "imageAmbient" && imageAmbientData?.status === 1) {
      return "image_ready";
    }

    if (isPlanetHdJobReady(imageAmbientHdData?.status)) {
      return "image_hd_ready";
    }

    if (imageAmbientData?.status === 1) {
      return "image_ready";
    }

    if (isPlanetHdJobReady(ambientHdData?.status)) {
      return "ready";
    }

    if (ambientData?.status === 1) {
      return "ready";
    }

    if (
      ambientData?.status === 2 ||
      imageAmbientData?.status === 2 ||
      isPlanetHdJobFailed(imageAmbientHdData?.status) ||
      isPlanetHdJobFailed(ambientHdData?.status)
    ) {
      return "failed";
    }

    return "empty";
  }, [ambientData, ambientHdData, ambientHdLoaded, ambientLoaded, imageAmbientData, imageAmbientHdData, imageAmbientHdLoaded, imageAmbientLoaded, loading, selectedElement]);

  const ambientSummary = useMemo(() => {
    const hasHistoricalAmbient = ambientData?.status === 1 && Boolean(ambientData?.geojson?.features?.length);
    const hasHistoricalHdAmbient = isPlanetHdJobReady(ambientHdData?.status) && Boolean(ambientHdData?.geojson?.features?.length);
    const hasImageAmbient = imageAmbientData?.status === 1 && Boolean(imageAmbientData?.geojson?.features?.length);
    const hasImageAmbientHd = isPlanetHdJobReady(imageAmbientHdData?.status) && Boolean(imageAmbientHdData?.geojson?.features?.length);
    const hasFailedHistoricalHdJob = isPlanetHdJobFailed(ambientHdData?.status);
    const hasFailedImageHdJob = isPlanetHdJobFailed(imageAmbientHdData?.status);
    const hasFailedHistoricalAmbient = ambientData?.status === 2;
    const hasFailedImageAmbient = imageAmbientData?.status === 2;
    const isHistoricalAmbientProcessing = ambientData?.status === 0;
    const isHistoricalHdAmbientProcessing = isPlanetHdJobPending(ambientHdData?.status);
    const isImageAmbientProcessing = imageAmbientData?.status === 0;
    const isImageHdAmbientProcessing = isPlanetHdJobPending(imageAmbientHdData?.status);

    return {
      sectionState: ambientSectionState,
      hasHistoricalAmbient,
      hasHistoricalHdAmbient,
      hasImageAmbient,
      hasImageAmbientHd,
      hasFailedHistoricalHdJob,
      hasFailedImageHdJob,
      hasFailedHdJob: hasFailedHistoricalHdJob || hasFailedImageHdJob,
      hasFailedHistoricalAmbient,
      hasFailedImageAmbient,
      hasFailedLegacyJob: hasFailedHistoricalAmbient || hasFailedImageAmbient,
      isHistoricalAmbientProcessing,
      isHistoricalHdAmbientProcessing,
      isImageAmbientProcessing,
      isImageHdAmbientProcessing,
      isProcessing: ambientSectionState === "processing",
      isReady: ambientSectionState === "ready",
      isImageReady: ambientSectionState === "image_ready",
      isImageHdReady: ambientSectionState === "image_hd_ready",
      isFailed: ambientSectionState === "failed",
      isEmpty: ambientSectionState === "empty",
      activeElement: selectedElement,
      isViewingHistoricalAmbient: selectedElement === "ambient",
      isViewingHistoricalHdAmbient: selectedElement === "ambientHd",
      isViewingImageAmbient: selectedElement === "imageAmbient",
      isViewingImageAmbientHd: selectedElement === "imageAmbientHd",
      canUpdateHistoricalAmbient: Boolean(ambientData?.editable),
    };
  }, [ambientData, ambientHdData, ambientSectionState, imageAmbientData, imageAmbientHdData, selectedElement]);

  const value = {
    canAccessHistoricalHd,
    loading,
    ambientLoaded,
    ambientHdLoaded,
    imageAmbientLoaded,
    imageAmbientHdLoaded,
    ambientSectionState,
    ambientSummary,
    loadAmbientData,
    loadAmbientHdData,
    ambientData,
    ambientHdData, setAmbientHdData,
    imageAmbientData, setImageAmbientData,
    imageAmbientHdData, setImageAmbientHdData,
    loadImageAmbientData,
    loadImageAmbientHdData,
    buildQueuedImageAmbientData,
    createTriggered,
    create,
    createHd,
    selectedElement: selectedElement === "ambientHd" && !canAccessHistoricalHd ? null : selectedElement,
    setSelectedElement: setSelectedElementForCurrentLand,

    ambientDialog: ambientDialog && (ambientDialogMode !== "hd" || canAccessHistoricalHd),
    setAmbientDialog, ambientDialogMode, openAmbientDialog,
    deleteAmbient, setDeleteAmbient,
    dialogInitialData, setDialogInitialData,
    updateAmbientDialog
  };

  return (
    <>
      <ViewAmbientContext.Provider value={value}>
        {children}
      </ViewAmbientContext.Provider>
    </>
  );
};
